import React, { useState } from 'react'
import { useLocation } from 'react-router-dom'
import { toast } from 'react-hot-toast'
import { useMutation } from 'react-query'

import { Place } from '../../types/utils'
import placeService from '../../services/placeService'
import { queryClient } from '../../utils/react-query'

import BreadCrumbs from '../BreadCrumbs'
import ClaimModal from './ClaimModal'
import DeleteButton from '../DeleteButton'
import { useUser } from '../../hooks/useUser'
import { BuzzlUsersError } from '../../types/buzzl-user'

type PendingViewProps = {
  data: Place
}

const PendingView: React.FC<PendingViewProps> = (props) => {
  const { pathname } = useLocation()
  const user = useUser()

  const canBuzzlUserEdit =
    (user.data?.type === 'BUZZL' && user.data.role === 'ADMIN') || //admin users can edit everything
    (user.data?.type === 'BUZZL' &&
      user.data.role === 'OPERATOR' &&
      user.data.buzzlUser?.assignedCustomers &&
      user?.data?.buzzlUser?.assignedCustomers?.length < 1) || //operator users with no assigned customer can edit everything
    user.data?.buzzlUser?.assignedCustomers?.some((customer) => customer.id === props.data.requestedClaimBy?.id) // if the customer is assigned to a buzzl user they can edit its location

  const [open, setOpen] = useState(false)
  const respondToClaimRequest = useMutation(placeService.respondToClaimRequest, {
    onSuccess: async () => {
      queryClient.refetchQueries(['location', props.data.id])
      queryClient.refetchQueries(['pending-locations'])
    },
    onError: (e) => {
      const error = e as BuzzlUsersError
      error.response.errors.map((er) =>
        toast.error(er.message || 'Something went wrong, if the error persist please contact support.')
      )
    },
  })

  const breadCrumbPages = [
    { name: 'Locations', href: '/locations' },
    { name: props.data.name, href: pathname },
  ]

  const handleClose = () => {
    setOpen(false)
  }

  const handleRespond = (action: 'APPROVE' | 'DENY' | 'CANCEL') => async () => {
    try {
      await respondToClaimRequest.mutateAsync({
        placeId: props.data.id,
        action: action,
      })
    } catch (e) {
      console.log(e)
    }
  }

  return (
    <div>
      <BreadCrumbs breadCrumbPages={breadCrumbPages} />
      <ClaimModal placeId={props.data.id} open={open} onClose={handleClose} />
      <div className="mb-6 flex justify-between">
        <h1 className="text-lg font-medium leading-6 text-gray-900">{props.data.name}</h1>
        {canBuzzlUserEdit ? (
          <div className="space-x-2">
            <DeleteButton
              className="btn btn-white"
              modalTitle="Deny Request?"
              modalText="You are about to deny the selected requests for those locations. Are you sure you want to deny all of those?"
              disabled={respondToClaimRequest.isLoading}
              onClick={handleRespond('DENY')}
            >
              Reject Claim
            </DeleteButton>
            <DeleteButton
              alert
              className="btn btn-primary"
              modalTitle="Accept Request?"
              modalText="You are about to approve the selected requests to add those locations. Are you sure you want to approve all of those?"
              disabled={respondToClaimRequest.isLoading}
              onClick={handleRespond('APPROVE')}
            >
              Accept Claim
            </DeleteButton>
          </div>
        ) : null}
      </div>
      <div className="overflow-hidden bg-white shadow sm:rounded-lg">
        <div className="flex justify-between px-4 py-5 sm:px-6">
          <h3 className="text-lg font-medium leading-6 text-gray-900">{props.data.name}</h3>
          <span className="tag alert-tag">Pending</span>
        </div>
        <div className="border-t border-gray-200 px-4 py-5 sm:p-0">
          <dl className="sm:divide-y sm:divide-gray-200">
            <div className="py-4 sm:grid sm:grid-cols-3 sm:gap-4 sm:py-5 sm:px-6">
              <dt className="text-sm font-medium text-gray-500">Address</dt>
              <dd className="mt-1 text-sm text-gray-900 sm:col-span-2 sm:mt-0">{props.data.address}</dd>
            </div>
            <div className="py-4 sm:grid sm:grid-cols-3 sm:gap-4 sm:py-5 sm:px-6">
              <dt className="text-sm font-medium text-gray-500">Customer</dt>
              <dd className="mt-1 text-sm text-gray-900 sm:col-span-2 sm:mt-0">{props.data.requestedClaimBy?.name}</dd>
            </div>
            <div className="py-4 sm:grid sm:grid-cols-3 sm:gap-4 sm:py-5 sm:px-6">
              <dt className="text-sm font-medium text-gray-500">Rating</dt>
              <dd className="mt-1 text-sm text-gray-900 sm:col-span-2 sm:mt-0">-</dd>
            </div>
          </dl>
        </div>
      </div>
    </div>
  )
}

export default PendingView
